'use client';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import Link from 'next/link';
import { Loader2, ExternalLink, CheckCircle2 } from 'lucide-react';
import { useState } from 'react';
import { saveBitbucketBotToken } from '@/apis/prReviewsConfig';

export const BITBUCKET_REQUIRED_SCOPES = [
  'read:account',
  'read:user:bitbucket',
  'read:repository:bitbucket',
  'read:pullrequest:bitbucket',
  'read:issue:bitbucket',
  'read:workspace:bitbucket',
  'read:webhook:bitbucket',
  'read:pipeline:bitbucket',
  'read:runner:bitbucket',
  'write:repository:bitbucket',
  'write:pullrequest:bitbucket',
  'write:issue:bitbucket',
  'write:webhook:bitbucket',
  'admin:project:bitbucket',
  'admin:repository:bitbucket',
] as const;

export const BITBUCKET_API_TOKEN_URL =
  'https://support.atlassian.com/bitbucket-cloud/docs/create-an-api-token/';

/** Entelligence docs: Bitbucket Cloud setup & troubleshooting */
export const BITBUCKET_ENTELLIGENCE_DOCS_URL =
  'https://docs.entelligence.ai/bitbucket-cloud';

const BitbucketAppPasswordModal = ({
  isOpen,
  onClose,
  onSuccess,
  orgUUID,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  orgUUID: string;
}) => {
  const [email, setEmail] = useState('');
  const [appPassword, setAppPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    setEmail('');
    setAppPassword('');
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!email || !appPassword) return;
    setIsLoading(true);
    setError(null);
    try {
      const res = await saveBitbucketBotToken({
        OrgUUID: orgUUID,
        BitbucketBotToken: appPassword,
        BitbucketBotEmail: email,
      });
      if (res.error) {
        setError(res.error);
      } else {
        onSuccess?.();
        handleClose();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-[95vw] md:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>Connect Bitbucket API Token</DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-4">
          <p className="text-sm text-ds-text-subtle">
            To enable PR Bot on your Bitbucket repositories, create a Bitbucket
            API token with the required scopes. Use your Bitbucket account email
            as the username and paste the API token below.
          </p>

          <div className="space-y-3 rounded-lg border border-ds-border-default bg-ds-surface-dark p-4">
            <p className="text-sm font-medium text-ds-text-subtle">
              Setup Guide
            </p>
            <ol className="list-decimal list-inside space-y-2 text-sm text-ds-text-subtle">
              <li>
                Go to{' '}
                <Link
                  href={BITBUCKET_API_TOKEN_URL}
                  className="text-ds-brand-default hover:underline inline-flex items-center gap-1"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Bitbucket API tokens
                  <ExternalLink className="h-3 w-3" />
                </Link>
              </li>
              <li>
                Click{' '}
                <span className="font-medium text-ds-text-subtle">
                  &quot;Create API token with scopes&quot;
                </span>
              </li>
              <li>
                Label it{' '}
                <span className="font-medium text-ds-text-subtle">
                  &quot;Entelligence PR Bot&quot;
                </span>
              </li>
              <li>
                Select{' '}
                <span className="font-medium text-ds-text-subtle">
                  Bitbucket
                </span>{' '}
                as the product
              </li>
              <li>
                Add these scopes:
                <div className="mt-1.5 ml-4 space-y-1">
                  {BITBUCKET_REQUIRED_SCOPES.map((scope) => (
                    <div key={scope} className="flex items-center gap-2">
                      <CheckCircle2 className="h-3.5 w-3.5 text-ds-brand-default shrink-0" />
                      <span className="font-mono text-xs text-ds-text-subtle sm:text-sm">
                        {scope}
                      </span>
                    </div>
                  ))}
                </div>
              </li>
              <li>
                Click{' '}
                <span className="font-medium text-ds-text-subtle">
                  &quot;Create&quot;
                </span>{' '}
                and copy the generated API token
              </li>
            </ol>
            <p className="border-t border-ds-border-default pt-3 text-sm text-ds-text-subtle">
              Questions? See the{' '}
              <Link
                href={BITBUCKET_ENTELLIGENCE_DOCS_URL}
                className="inline-flex items-center gap-1 font-medium text-ds-brand-default hover:underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Bitbucket Cloud guide
                <ExternalLink className="h-3 w-3" />
              </Link>
              .
            </p>
          </div>

          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Bitbucket Account Email (required)</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your-email@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label>API Token (required)</Label>
              <Input
                type="password"
                value={appPassword}
                onChange={(e) => setAppPassword(e.target.value)}
                placeholder="Paste your Bitbucket API token here"
              />
            </div>
          </div>

          {error && <p className="text-sm text-ds-danger-default">{error}</p>}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="ghost" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="secondary"
            disabled={!email || !appPassword || isLoading}
          >
            {isLoading ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              'Connect'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BitbucketAppPasswordModal;
